import { ReactNode, useState } from "react";

import { Button, Classes, Collapse, Colors, Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";

import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { MIDTEXT_COLOR } from "@/css/constants";

const BOX_PADDING = 24;

interface ProgramCardProps {
  isCompleted?: boolean;
  title: string;
  description?: string;
  subTitle?: ReactNode;
  image?: string; // or appropriate image type
  buttonText?: string;
  secondaryButton?: ReactNode;
  onAddClick?: () => void;
  defaultVisible?: boolean;
  onViewCard?: () => void;
  children?: ReactNode;
  isChildrenBelow?: boolean;
  actionView?: ReactNode;
  hasStatusIcon?: boolean;
  contentPadding?: number;
  titleClassName?: string;
  canCollapse?: boolean;
}

export function ProgramCard({
  isCompleted,
  title,
  description,
  subTitle,
  image,
  buttonText,
  secondaryButton,
  onAddClick,
  defaultVisible = false,
  onViewCard,
  children,
  isChildrenBelow = false,
  actionView,
  hasStatusIcon = true,
  contentPadding = BOX_PADDING,
  titleClassName = "program-card-header",
  canCollapse = true,
}: ProgramCardProps) {
  const [cardDetailsVisible, setCardDetailsVisible] = useState(defaultVisible);

  return (
    <StyledBox
      flex={1}
      mt={24}
      className={`bas round-corners ${
        cardDetailsVisible && `active-item ${Classes.ELEVATION_1}`
      }`}
    >
      <Flex
        className={`${titleClassName} ${!canCollapse && "disable-collapse"}`}
        onClick={() => {
          if (canCollapse) {
            if (!cardDetailsVisible) {
              onViewCard?.();
            }
            setCardDetailsVisible(!cardDetailsVisible);
          }
        }}
      >
        <Box alignSelf={"flex-start"}>
          {hasStatusIcon &&
            (isCompleted ? (
              <Icon icon="tick-circle" color={Colors.GREEN1} size={16} />
            ) : (
              <Icon icon="circle" color="#BF7326" size={16} />
            ))}
        </Box>
        <Box mx={12} flex={1}>
          <Box fontSize={18} fontWeight={600}>
            {title}
          </Box>
          {subTitle && (
            <Box fontSize={14} color={MIDTEXT_COLOR}>
              {subTitle}
            </Box>
          )}
        </Box>

        <Flex
          justifyContent="space-between"
          alignItems="center"
          style={{
            gap: "2rem",
          }}
        >
          {actionView}
          {canCollapse && (
            <Icon
              icon={cardDetailsVisible ? "chevron-up" : "chevron-down"}
              size={16}
            />
          )}
        </Flex>
      </Flex>
      <CollapseContainer>
        <Collapse isOpen={cardDetailsVisible} keepChildrenMounted={false}>
          <Box p={contentPadding} className="bts">
            {!isChildrenBelow && children}
            <Flex
              justifyContent="space-between"
              flexDirection={["column-reverse", "column-reverse", "row", "row"]}
            >
              <Box flex={1} maxWidth={350}>
                {description && <Box mb={24}>{description}</Box>}
                {buttonText && (
                  <Button intent="primary" large onClick={onAddClick}>
                    {buttonText}
                  </Button>
                )}
                {secondaryButton && (
                  <Box style={{ marginLeft: "16px", display: "inline" }}>
                    {secondaryButton}
                  </Box>
                )}
              </Box>
              <Flex width={240} alignItems={"center"} margin={"0 auto"}>
                {image && (
                  <EmbedSvg src={image} height={"auto"} width={"100%"} />
                )}
              </Flex>
            </Flex>
            {isChildrenBelow && children}
          </Box>
        </Collapse>
      </CollapseContainer>
    </StyledBox>
  );
}

const CollapseContainer = styled.div`
  position: relative;
  overflow: hidden;
`;

const StyledBox = styled(Box)`
  &:hover {
    box-shadow:
      0 1px 1px rgba(24, 32, 38, 0.1),
      0 2px 6px rgba(24, 32, 38, 0.05);
  }
  &.active-item {
    box-shadow:
      0 1px 1px rgba(24, 32, 38, 0.1),
      0 2px 6px rgba(24, 32, 38, 0.05);
  }

  .program-card-header {
    align-items: center;
    width: 100%;
    cursor: pointer;
    padding: ${BOX_PADDING}px;
    border-radius: 6px;

    &:hover {
      background: #fcfcff;
    }

    &.disable-collapse {
      cursor: default;

      &:hover {
        background: none;
      }
    }

    &.active-item {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    &.mini-item {
      padding: 16px;
    }
  }
`;
